import React from 'react'
import 'views/views.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquare } from '@fortawesome/free-solid-svg-icons'
import {
    BarChart as RechartBarChart,
    XAxis,
    YAxis,
    Tooltip,
    Bar,
    ResponsiveContainer,
    Legend,
    LabelList,
    Cell,
    // CartesianGrid,
} from 'recharts'
import { LoadingIndicator } from 'components'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'

/**
 * @name BarChart
 * @desc BarChart wrapper
 * @param chartTitle - String that represents the title of the chart
 * @param chartActions - Extended functionality that can exist in the header of the chart container - for example a 'update range' button
 * @param data - Array of data, containing [{name: foo, value: 99},...]
 * @param loading - Bool
 */
const BarChart = ({
    data = [],
    loading = false,
    valueDataKey = undefined,
    barLineColor = '#b71c1c',
    showLegend = false,
    stackedBarValueKeys = [],
    colorPalette = [],
    dataValuePercent = false,
    height = 225,
    xAxisAngle = false,
    tooltipPercent = false,
    disableAutoDomain = false,
    customDomain = undefined,
    enableVerticalChart = true,
    highlightLowestBar = false,
}: {
    data: any[]
    loading?: boolean
    valueDataKey?: undefined | string
    barLineColor?: string
    showLegend?: boolean
    stackedBarValueKeys?: [string, number][]
    colorPalette?: string[]
    dataValuePercent?: boolean
    height?: number
    xAxisAngle?: boolean
    tooltipPercent?: boolean
    disableAutoDomain?: boolean
    customDomain?: [number, number] // Custom domain for Y-axis [min, max]
    enableVerticalChart?: boolean
    highlightLowestBar?: boolean
}): React.ReactElement => {
    const { clientTheme } = useThemeStyling()

    const renderHorizontalLabel = ({ x, y, value }: { [key: string]: any }) => {
        return (
            <g>
                <text
                    x={x + 5}
                    y={y - 3}
                    textAnchor="left"
                    fill={
                        clientTheme.typography.fontColor.primaryText ||
                        '#263238'
                    }
                    fontSize={clientTheme.typography.body1.fontSize}
                >
                    {value}
                </text>
            </g>
        )
    }

    const CustomTooltipPercent = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div
                    className={'emp-barChart-tooltipContainer'}
                    style={{ fontSize: clientTheme.typography.body1.fontSize }}
                >
                    <table style={{ borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr style={{ backgroundColor: '#b3b3b3' }}>
                                <td
                                    className={
                                        'emp-barChart-tooltipTableHeader'
                                    }
                                >
                                    {label}
                                </td>
                            </tr>

                            <tr>
                                <td
                                    className={'emp-barChart-tooltipTableRow'}
                                    style={{
                                        textAlign: 'center',
                                    }}
                                >
                                    {tooltipPercent
                                        ? formatPercentTick(payload[0].value)
                                        : payload[0].value}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )
        }

        return null
    }

    const CustomTooltipValue = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div
                    className={'emp-barChart-tooltipContainer'}
                    style={{ fontSize: clientTheme.typography.body1.fontSize }}
                >
                    <table style={{ borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr style={{ backgroundColor: '#b3b3b3' }}>
                                <td
                                    colSpan={2}
                                    className={
                                        'emp-barChart-tooltipTableHeader'
                                    }
                                >
                                    {label}
                                </td>
                            </tr>
                            {payload.map((item: any, i: number) => {
                                return (
                                    <tr key={`key-tooltip-${i}`}>
                                        <td
                                            className={
                                                'emp-barChart-tooltipTableRow'
                                            }
                                            style={{
                                                borderRight:
                                                    '1px dotted #b3b3b3',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                size="xs"
                                                icon={faSquare}
                                                style={{
                                                    marginRight: '5px',
                                                    color: colorPalette.length
                                                        ? payload[i].fill
                                                        : barLineColor,
                                                }}
                                            />
                                            {payload[i].name}
                                        </td>
                                        <td
                                            className={
                                                'emp-barChart-tooltipTableRow'
                                            }
                                            style={{
                                                textAlign: 'right',
                                            }}
                                        >
                                            {payload[i].value}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )
        }
        return null
    }

    // Format percentage values based on their magnitude
    const formatPercentTick = (val: number): string => {
        if (val === 0) {
            return '0%'
        }

        // For small values (less than 1%), show 2 decimal places
        if (val < 1) {
            return `${val.toFixed(2)}%`
        }

        // For larger values, show 1 decimal place
        return `${val.toFixed(1)}%`
    }

    // const toPercent = (decimal: number) => `${decimal * 100}%`

    const minValue = highlightLowestBar
        ? Math.min(...data.map((item) => item.value))
        : 0

    return (
        <div className={'emp-barChart-root'}>
            {!loading ? (
                <>
                    {data.length ? (
                        <ResponsiveContainer height={height} width="100%">
                            {enableVerticalChart ? (
                                <RechartBarChart
                                    data={data}
                                    stackOffset={
                                        dataValuePercent ? 'none' : 'expand'
                                    }
                                    margin={{
                                        top: 20,
                                        left: -10,
                                        bottom: 5,
                                        right: xAxisAngle ? 25 : 0,
                                    }}
                                >
                                    {/* <CartesianGrid strokeDasharray="3 3" /> */}
                                    {xAxisAngle ? (
                                        <XAxis
                                            dataKey="name"
                                            angle={45}
                                            textAnchor="start"
                                            height={70}
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                            interval={0}
                                        />
                                    ) : (
                                        <XAxis
                                            dataKey="name"
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                        />
                                    )}
                                    {dataValuePercent ? (
                                        <YAxis
                                            tickFormatter={formatPercentTick}
                                            domain={
                                                customDomain ||
                                                (disableAutoDomain
                                                    ? undefined
                                                    : dataValuePercent
                                                    ? [0, 100]
                                                    : undefined)
                                            }
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                        />
                                    ) : (
                                        <YAxis
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                        />
                                    )}
                                    {dataValuePercent ? (
                                        <Tooltip
                                            content={<CustomTooltipPercent />}
                                            cursor={{ fill: '#f5f5f5' }}
                                        />
                                    ) : (
                                        <Tooltip
                                            content={<CustomTooltipValue />}
                                            cursor={{ fill: '#f5f5f5' }}
                                        />
                                    )}
                                    {dataValuePercent ||
                                    !stackedBarValueKeys.length ? (
                                        <Bar
                                            dataKey={
                                                valueDataKey
                                                    ? valueDataKey
                                                    : 'val'
                                            }
                                            fill={barLineColor}
                                            maxBarSize={100}
                                        >
                                            {' '}
                                            {data.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={
                                                        highlightLowestBar &&
                                                        entry.value === minValue
                                                            ? clientTheme.charts
                                                                  ?.barCharts
                                                                  ?.lowestBar ??
                                                              clientTheme.primary
                                                            : barLineColor
                                                    }
                                                />
                                            ))}
                                        </Bar>
                                    ) : (
                                        stackedBarValueKeys.map(
                                            (bar: any, index: number) => {
                                                return (
                                                    <Bar
                                                        dataKey={bar[0]}
                                                        key={`${index}_bar-line`}
                                                        fill={
                                                            colorPalette[
                                                                index %
                                                                    colorPalette.length
                                                            ] || '#b71c1c'
                                                        }
                                                        stackId="a"
                                                        maxBarSize={100}
                                                    />
                                                )
                                            }
                                        )
                                    )}

                                    {showLegend ? (
                                        <Legend
                                            verticalAlign="bottom"
                                            align="center"
                                            height={55}
                                            wrapperStyle={{
                                                left: 27,
                                                fontSize:
                                                    clientTheme.typography.body1
                                                        .fontSize,
                                            }}
                                        />
                                    ) : null}
                                </RechartBarChart>
                            ) : (
                                <RechartBarChart
                                    data={data}
                                    stackOffset={
                                        dataValuePercent ? 'none' : 'expand'
                                    }
                                    margin={{
                                        top: 20,
                                        left: -50,
                                        bottom: 5,
                                        right: 0,
                                    }}
                                    layout="vertical"
                                    barCategoryGap="27%"
                                >
                                    {/* <CartesianGrid strokeDasharray="3 3" /> */}

                                    <YAxis
                                        dataKey="name"
                                        tick={false}
                                        type="category"
                                        tickLine={false}
                                    />

                                    {dataValuePercent ? (
                                        <XAxis
                                            tickFormatter={formatPercentTick}
                                            domain={
                                                customDomain ||
                                                (disableAutoDomain
                                                    ? undefined
                                                    : dataValuePercent
                                                    ? [0, 100]
                                                    : undefined)
                                            }
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                            type="number"
                                        />
                                    ) : (
                                        <XAxis
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                            type="number"
                                        />
                                    )}
                                    {dataValuePercent ? (
                                        <Tooltip
                                            content={<CustomTooltipPercent />}
                                            cursor={{ fill: '#f5f5f5' }}
                                        />
                                    ) : (
                                        <Tooltip
                                            content={<CustomTooltipValue />}
                                            cursor={{ fill: '#f5f5f5' }}
                                        />
                                    )}
                                    {dataValuePercent ||
                                    !stackedBarValueKeys.length ? (
                                        <Bar
                                            dataKey={
                                                valueDataKey
                                                    ? valueDataKey
                                                    : 'val'
                                            }
                                            fill={barLineColor}
                                            maxBarSize={100}
                                        >
                                            {data.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={
                                                        highlightLowestBar &&
                                                        entry.value === minValue
                                                            ? '#cf2b24'
                                                            : barLineColor
                                                    }
                                                />
                                            ))}
                                            <LabelList
                                                dataKey="name"
                                                content={renderHorizontalLabel}
                                            />
                                        </Bar>
                                    ) : (
                                        stackedBarValueKeys.map(
                                            (bar: any, index: number) => {
                                                return (
                                                    <Bar
                                                        dataKey={bar[0]}
                                                        key={`${index}_bar-line`}
                                                        fill={
                                                            colorPalette[
                                                                index %
                                                                    colorPalette.length
                                                            ] || '#b71c1c'
                                                        }
                                                        stackId="a"
                                                        maxBarSize={100}
                                                    >
                                                        <LabelList
                                                            dataKey="name"
                                                            content={
                                                                renderHorizontalLabel
                                                            }
                                                        />
                                                    </Bar>
                                                )
                                            }
                                        )
                                    )}

                                    {showLegend ? (
                                        <Legend
                                            verticalAlign="bottom"
                                            align="center"
                                            height={55}
                                            wrapperStyle={{
                                                left: 27,
                                                fontSize:
                                                    clientTheme.typography.body1
                                                        .fontSize,
                                            }}
                                        />
                                    ) : null}
                                </RechartBarChart>
                            )}
                        </ResponsiveContainer>
                    ) : (
                        <div className={'emp-barChart-noDataMessaging'}>
                            No Data
                        </div>
                    )}
                </>
            ) : (
                <div className={'emp-barChart-loadingContainer'}>
                    <LoadingIndicator />
                </div>
            )}
        </div>
    )
}

export default BarChart
